import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getStores } from '../../api/storeApi';
import { DC_LOCATIONS } from '../../constants/stores';
import { useDebounce } from '../../Hooks/useDebounce';
import { BolData } from '../../interface/BillOfLading';
import { generatePDF } from '../../Utils/generatePDF';
import { getDayOfWeek } from '../../Utils/getDaybyDate';
import { getStoreInfo } from '../../Utils/utils';

import { CheckIcon } from '@mantine/core';
import { createBolApi, getApprovalBolList, updateBol, updateBolApproval, updateBolStatus } from '../../api/bolApi';
import { downloadFile } from '../../api/invoiceApi';
import BolFilterModal from '../../components/Bol/BolFilterModal';
import BolWorkspace from '../../components/Bol/BolWorkspace';
import AppDialogMessage from '../../components/Common/AppDialogMessage';
import GenerateInvoice from '../../components/Invoice/GenerateInvoice';
import BolPrint from '../../components/Print/BolPrint';
import BolReportDownload from '../../components/Print/BolReport';
import { useAppDialog } from '../../Hooks/useAppDialog';

const initialNewBol: BolData = {
    id: "",
    dclocation: "",
    third_party: null,
    bol_type: "",
    shipper: {
        name: "",
        address: "",
        postal_code: "",
        sid_no: "",
    },
    receiver: {
        name: "",
        address: "",
        postal_code: "",
        cid_no: "",
    },
    po: [],
    bol_no: "",
    amount: null,
    type: "",
    dc_received: false,
    vendor_delivered: false,
    payment_status: false,
    cancel: false,
    approval: false,
    approved_by: null,
    corporation_no: '',
    trailer: "",
    supplier: "",
    bol_date: "",
    note: "",
    description: "",
    info: null,
    invoice_no: "",
    invoice_file: "",
    created_at: "",
    updated_at: "",
};

const initialFilter = {
    shipper: '',
    receiver: '',
    po_no: '',
    bol_no: '',
    type: '',
    invoice_no: '',
    vendor_delivered: null as boolean | null,
    dc_received: null as boolean | null,
    approved: null as boolean | null,
    payment_status: null as boolean | null,
    trailer: '',
    supplier: '',
    start_date: '',
    end_date: '',
    info: '',
};

export default function BillOfLading() {
    const user = useSelector((state: any) => state.user.user);
    const [trigger, setTrigger] = useState(false);
    const [bolList, setBolList] = useState<any[]>([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 1,
        totalRows: 0,
    });

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);

    const [filter, setFilter] = useState(initialFilter);
    const [filterModal, setFilterModal] = useState(false);

    const [createModal, setCreateModal] = useState(false);
    const [openModal, setOpenModal] = useState(false);

    const [newBol, setNewBol] = useState<any>(initialNewBol);
    const [bol, setBol] = useState<BolData>(initialNewBol);

    const [dcLocation, setDcLocation] = useState<any>(user.location);
    const [storeList, setStoreList] = useState<any[]>([]);
    const [storeOptions, setStoreOptions] = useState<{ label: string; value: string }[]>([]);
    const [locationName, setLocationName] = useState<string>('');

    const { dialog, closeDialog, openDialog } = useAppDialog();
    // Setup store list and options for dropdowns
    const setupStoreList = async () => {
        try {
            const data = await getStores();
            setStoreList(data);
            setStoreOptions([
                { label: 'Other', value: 'Other' },
                ...data.map((store: any) => ({ label: store.location, value: store.location })),
            ]);
            setLocationName(getStoreInfo('location', user.location, data).name);
            localStorage.setItem('storeList', JSON.stringify(data));
        } catch (error) {
            console.log('Error Setting up the stores:', error);
        }
    }
    useEffect(() => {
        setupStoreList();
    }, []);

    useEffect(() => {
        fetchBolList(1, pagination.limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, dcLocation]);

    useEffect(() => {
        setFilter((prev) => ({ ...prev, bol_no: debouncedSearch }));
        setTrigger(!trigger);
    }, [debouncedSearch]);

    useEffect(() => {
        fetchBolList(pagination.page, pagination.limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, trigger]);

    const fetchBolList = async (page: number, limit: number) => {
        try {
            const result = await getApprovalBolList({
                page,
                limit,
                dclocation: dcLocation === 'All' ? '' : dcLocation,
                filters: filter,
            });

            setBolList(result.data || []);

            setPagination((prev) => ({
                ...prev,
                page,
                total: Math.ceil((result.totalRows || 0) / limit) || 1,
                totalRows: result.totalRows || 0,
            }));
        } catch (error) {
            console.log('Error in getApprovalBolList:', error);
        }
    };

    // const approvalCheck = (location: string) => {
    //     if (locationName === 'SVP SPORTS DCCAL' && location === 'SVP SPORTS DC') return true;
    //     if (locationName === 'SVP SPORTS DC' && location === 'SVP SPORTS DCCAL') return true;
    //     return false;
    // };

    const handleOpenModal = (row: BolData) => {
        setBol(row);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setBol(initialNewBol);
        setOpenModal(false);
    };

    const handleCloseCreateModal = () => {
        setCreateModal(false);
        setNewBol(initialNewBol);
    };

    const handleFilterChange = (key: string, value: any) => {
        setFilter((prev) => ({ ...prev, [key]: value }));
    };

    const handleApplyFilter = () => {
        setPagination((prev) => ({ ...prev, page: 1 }));
        fetchBolList(1, pagination.limit);
        setFilterModal(false);
    };

    const handleResetFilter = () => {
        setFilter(initialFilter);
        setPagination((prev) => ({ ...prev, page: 1 }));
        setFilterModal(false);
        setSearch('');
        setTimeout(() => fetchBolList(1, pagination.limit), 0);
    };

    const handleRowUpdate = (id: string, data: any) => {
        setBolList((prev) => prev.map((item: any) => (item.id === id ? data : item)));
    };

    const handleUpdateBol = async () => {
        try {
            const result = await updateBol(bol);

            handleCloseModal();
            handleRowUpdate(bol.id, result.data[0]);

            openDialog({
                variant: 'success',
                title: 'Updated',
                message: result.message || 'BOL updated successfully',
                hideCancel: true,
            });
        } catch (error: any) {
            console.log('Error in handleUpdateBol:', error);

            openDialog({
                variant: 'error',
                title: 'Update Failed',
                message: error?.message || 'Error in updating BOL',
                hideCancel: true,
            });
        }
    };

    const handleApproval = async (status: boolean | null, bol_no: string) => {
        try {
            const result = await updateBolApproval({ status, bol_no });

            // remove approved/rejected BOL from approval list
            setBolList((prev) =>
                prev.filter((item: any) => item.bol_no !== bol_no)
            );

            openDialog({
                variant: 'success',
                title: 'Approved',
                message: result.message || `${bol_no} processed successfully.`,
                confirmText: 'Close',
                hideCancel: true,
            });
        } catch (error: any) {
            console.log('Error in approving BOL:', error);

            openDialog({
                variant: 'error',
                title: 'Action Failed',
                message: error?.message || 'Something went wrong.',
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };

    const handleStatusChange = async (status: boolean | null, bol_no: string) => {
        try {
            const result = await updateBolStatus({
                status,
                bol_no,
                column: 'dc_received',
            });

            setBolList((prev) =>
                prev.map((item: any) =>
                    item.bol_no === bol_no
                        ? { ...item, dc_received: result.data?.[0]?.dc_received }
                        : item
                )
            );

            openDialog({
                variant: 'success',
                title: 'Status Updated',
                message: result.message || 'Status updated successfully.',
                confirmText: 'Close',
                hideCancel: true,
            });
        } catch (error: any) {
            console.log('Error in updating status:', error);

            openDialog({
                variant: 'error',
                title: 'Update Failed',
                message: error?.message || 'Error updating status.',
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };

    const handleDownloadInvoice = async (invoice_file: string | null) => {
        if (!invoice_file) return;

        try {
            await downloadFile(invoice_file);
        } catch (error) {
            console.error('Error downloading file:', error);

            openDialog({
                variant: 'error',
                title: 'Download Failed',
                message: 'File not found or error downloading file.',
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };


    const handlePoUpdate = (
        type: 'po_no' | 'skid',
        index: number,
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value;
        const updatedPo = [...(newBol.po || [])];

        if (!updatedPo[index]) return;

        if (type === 'po_no') {
            updatedPo[index][type] = value;
        } else {
            updatedPo[index][type] = parseInt(value || '0', 10);
        }

        setNewBol((prev: any) => ({
            ...prev,
            po: updatedPo,
        }));
    };
    const handleExistingPoUpdate = (
        type: 'po_no' | 'skid',
        index: number,
        value: string
    ) => {
        const updatedPo = [...(bol.po || [])];
        if (!updatedPo[index]) return;

        if (type === 'po_no') {
            updatedPo[index][type] = value;
        } else {
            updatedPo[index][type] = parseInt(value || '0', 10);
        }

        setBol((prev) => ({ ...prev, po: updatedPo }));
    };

    const handleAddPo = () => {
        setBol((prev: any) => ({
            ...prev,
            po: prev.po === null ? [{ po_no: '', skid: '' }] : [...prev.po, { po_no: '', skid: '' }],
        }));
    };

    const handleRemovePo = (index: number) => {
        setBol((prev: any) => {
            const next = [...prev.po];
            next.splice(index, 1);
            return { ...prev, po: next };
        });
    };

    const handleReceiverLocationChange = (value: string | null) => {
        if (!value || value === 'Other') return;

        const shippingDetails = getStoreInfo('location', value, storeList);

        setNewBol((prev: any) => ({
            ...prev,
            to: shippingDetails.abbreviation,
            note: prev.note || shippingDetails.notes || '',
            receiver: {
                ...prev.receiver,
                name: shippingDetails.name,
                address: shippingDetails.address,
                postal_code: `${shippingDetails.city} ${shippingDetails.postal_code}`,
            },
        }));
    };
    const handleBolTypeChange = (value: string | null) => {
        setNewBol((prev: any) => ({
            ...prev,
            bol_type: value || '',
            supplier: value === 'EXTERNAL' ? prev.supplier || '' : '',
        }));
    };
    const handleShipperLocationChange = (value: string | null) => {
        if (!value || value === 'Other') return;

        const shippingDetails = getStoreInfo('location', value, storeList);

        setNewBol((prev: any) => ({
            ...prev,
            from: shippingDetails.abbreviation,
            note: prev.note || shippingDetails.notes || '',
            shipper: {
                ...prev.shipper,
                name: shippingDetails.name,
                address: shippingDetails.address,
                postal_code: `${shippingDetails.city} ${shippingDetails.postal_code}`,
            },
        }));
    };

    const createBolHandler = async () => {
        // validation stays unchanged
        if (!newBol.bol_type || !newBol.bol_date || !newBol.trailer || !newBol.po) {
            alert('Please fill all the required fields.');
            return;
        }

        if (newBol.bol_type === 'EXTERNAL') {
            if (!newBol.supplier) {
                alert('Please fill all the required fields');
                return;
            }
        } else {
            if (
                !newBol?.shipper?.name ||
                !newBol?.receiver?.name ||
                !newBol?.shipper?.address ||
                !newBol?.receiver?.address ||
                !newBol?.shipper?.postal_code ||
                !newBol?.receiver?.postal_code
            ) {
                alert('Please fill all the required fields');
                return;
            }
        }

        try {
            const result = await createBolApi(newBol);
            const createdBol = result.data;

            setBolList((prev) => [createdBol, ...prev]);

            handleCloseCreateModal();

            await generatePDF(
                createdBol,
                user,
                getStoreInfo('location', user.location, storeList),
                storeList
            );

            openDialog({
                variant: 'success',
                title: 'BOL Created',
                message: `${createdBol.bol_no} created successfully`,
                confirmText: 'Close',
                hideCancel: true,
            });
        } catch (error: any) {
            console.log('Error in createBol:', error);

            openDialog({
                variant: 'error',
                title: 'Create Failed',
                message: error?.message || 'Error in creating BOL',
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };

    const columns = useMemo(
        () => [
            {
                key: 'bol_no',
                label: 'BOL No',
                render: (row: any) => <BolPrint bol={row} size="xs" variant="outline" textElement={row?.bol_no} />,
            },
            {
                key: 'shipper',
                label: 'Shipper',
                render: (row: any) => `${row.shipper?.name} (${row.shipper?.postal_code?.split(',')[0] || ''})`,
            },
            {
                key: 'receiver',
                label: 'Receiver',
                render: (row: any) => `${row.receiver?.name} (${row.receiver?.postal_code?.split(',')[0] || ''})`,
            },
            {
                key: 'po',
                label: 'PO No.',
                render: (row: any) => row.po?.map((po: any) => po.po_no).join('/ '),
            },
            {
                key: 'bol_date',
                label: 'Date',
                render: (row: any) => (
                    <Stack spacing={0}>
                        <Typography variant="body2">{row.bol_date?.split('T')[0] ?? ''}</Typography>
                        <Typography variant="caption" fontWeight={700}>
                            {getDayOfWeek(row.bol_date) ?? ''}
                        </Typography>
                    </Stack>
                ),
            },
            {
                key: 'amount',
                label: 'Rate',
                render: (row: any) => `$${row?.amount ?? ''}`,
            },
            {
                key: 'type',
                label: 'Type',
            },
            {
                key: 'vendor_delivered',
                label: 'Vendor Delivered',
                render: (row: any) => (row?.vendor_delivered ? <Chip label="Yes" color={row?.cancel ? "warning" : "success"} variant="outlined" /> : row?.vendor_delivered === false ? <Chip label="No" color={row?.cancel ? "warning" : "error"} variant="outlined" /> : null)
            },
            {
                key: 'dc_received',
                label: 'DC Status',
                render: (row: any) => (
                    <TextField
                        select
                        size="small"
                        value={row.dc_received === true ? 'RECEIVED' : 'PENDING'}
                        disabled={row.cancel}
                        onChange={(e) => handleStatusChange(e.target.value === 'RECEIVED', row.bol_no)}
                        sx={{ minWidth: 110 }}
                    >
                        <MenuItem value="PENDING">Pending</MenuItem>
                        <MenuItem value="RECEIVED">Received</MenuItem>
                    </TextField>
                ),
            },
            {
                key: 'payment_status',
                label: 'Payment',
                render: (row: any) => (row?.payment_status ? 'Paid' : 'Unpaid'),
            },
            {
                key: 'trailer',
                label: 'Carrier',
            },
            {
                key: 'invoice_no',
                label: 'Invoice',
                render: (row: any) =>
                    row?.invoice_no ? <GenerateInvoice bol={row} textElement={row.invoice_no} /> : <Typography variant="body2">N/A</Typography>,
            },
            {
                key: 'invoice_file',
                label: 'Signed BOL',
                render: (row: any) =>
                    row?.invoice_file ? (
                        <Typography
                            variant="body2"
                            sx={{ cursor: 'pointer', fontWeight: 700 }}
                            onClick={() => handleDownloadInvoice(row.invoice_file)}
                        >
                            View
                        </Typography>
                    ) : (
                        <Typography variant="body2">N/A</Typography>
                    ),
            },
            {
                key: 'approval',
                label: 'Approve',
                render: (row: any) => {
                    const loc = getStoreInfo('name', row.receiver?.name, storeList);
                    // const approval = approvalCheck(loc);

                    if (row?.invoice_no === '' || row?.invoice_no === null || !row?.dc_received || !row?.vendor_delivered) {
                        return <Typography variant="body2">N/A</Typography>;
                    }

                    return (
                        // row?.approval ? <Chip label="Approved" color="success" variant="outlined" /> : <Button startIcon={<CheckIcon color='inherit' />} onClick={() => handleApproval(true, row.bol_no)}>Approve</Button>
                        <Button
                            variant='contained'
                            color='success'
                            size='small'
                            startIcon={<CheckIcon color="inherit" />}
                            onClick={() => handleApproval(true, row.bol_no)}
                            disabled={row.cancel ? true : row?.payment_status}
                        // disabled={row.cancel ? true : approval || row?.payment_status}
                        >Approve</Button>
                        // <Checkbox
                        //     checked={!!row?.approval}
                        //     disabled={row.cancel ? true : approval || row?.payment_status}
                        //     onChange={(e) => handleApproval(e.target.checked, row.bol_no)}
                        // />
                    );
                },
            },
        ],
        [storeList]
    );


    const handleSearch = (value: string) => {
        setSearch(value);
        setPagination((prev: any) => ({
            ...prev,
            page: 1,
        }));
    };

    const handleLocationChange = (value: string) => {
        setDcLocation(value);
        setPagination((prev: any) => ({
            ...prev,
            page: 1,
        }));
    };

    const renderRowActions = (_row: any) => null;

    return (
        <>
            <BolFilterModal
                open={filterModal}
                onClose={() => setFilterModal(false)}
                filters={filter}
                onChange={handleFilterChange}
                onApply={handleApplyFilter}
                onReset={handleResetFilter}
            />

            <BolWorkspace
                data={bolList}
                columns={columns}
                pagination={pagination}
                setPagination={setPagination}
                search={search}
                setSearch={handleSearch}
                allowLocation
                dcLocation={dcLocation}
                onLocationChange={handleLocationChange}
                onOpenFilter={() => setFilterModal(true)}
                showCreateButton
                onOpenCreate={() => setCreateModal(true)}
                onEdit={handleOpenModal}
                rowActions={(row) => renderRowActions(row)}
                bottomRightSlot={<BolReportDownload />}
            />

            {/* Create Modal */}
            <Dialog
                open={createModal}
                onClose={handleCloseCreateModal}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>Create</DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {/* BOL Type */}
                        <ToggleButtonGroup
                            value={newBol.bol_type || 'INTERNAL'}
                            exclusive
                            fullWidth
                            onChange={(_, value) => {
                                if (!value) return;
                                handleBolTypeChange(value);
                            }}
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'divider',
                                '& .MuiToggleButton-root': {
                                    border: 'none',
                                },
                                '& .MuiToggleButton-root.Mui-selected': {
                                    backgroundColor: theme => theme.palette.primary.main,
                                    color: theme => theme.palette.primary.contrastText,
                                    fontSize: '1rem',
                                },
                            }}
                        >
                            <ToggleButton
                                value="INTERNAL"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                Internal
                            </ToggleButton>

                            <ToggleButton
                                value="EXTERNAL"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                External
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {/* External Supplier */}
                        {newBol.bol_type === 'EXTERNAL' && (
                            <TextField
                                fullWidth
                                label="Supplier"
                                placeholder="Enter supplier"
                                value={newBol.supplier || ''}
                                onChange={(e) =>
                                    setNewBol((prev: any) => ({
                                        ...prev,
                                        supplier: e.target.value,
                                    }))
                                }
                            />
                        )}

                        {/* Shipper / Receiver */}
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Stack spacing={2}>
                                        <Typography variant="h6">Shipper</Typography>

                                        <TextField
                                            select
                                            fullWidth
                                            label="Select Location"
                                            value={newBol.fromLocation || ''}
                                            onChange={(e) => {
                                                setNewBol((prev: any) => ({
                                                    ...prev,
                                                    fromLocation: e.target.value,
                                                }));
                                                handleShipperLocationChange(e.target.value);
                                            }}
                                        >
                                            {storeOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        <TextField
                                            fullWidth
                                            label="Name"
                                            placeholder="Enter shipper"
                                            value={newBol.shipper?.name || ''}
                                            onChange={(e) =>
                                                setNewBol((prev: any) => ({
                                                    ...prev,
                                                    shipper: { ...prev.shipper, name: e.target.value },
                                                }))
                                            }
                                        />

                                        <TextField
                                            fullWidth
                                            label="Address"
                                            placeholder="Enter shipper address"
                                            value={newBol.shipper?.address || ''}
                                            onChange={(e) =>
                                                setNewBol((prev: any) => ({
                                                    ...prev,
                                                    shipper: { ...prev.shipper, address: e.target.value },
                                                }))
                                            }
                                        />

                                        <TextField
                                            fullWidth
                                            label="City, Postal Code"
                                            placeholder="Enter shipper postal code"
                                            value={newBol.shipper?.postal_code || ''}
                                            onChange={(e) =>
                                                setNewBol((prev: any) => ({
                                                    ...prev,
                                                    shipper: { ...prev.shipper, postal_code: e.target.value },
                                                }))
                                            }
                                        />
                                    </Stack>
                                </Paper>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Stack spacing={2}>
                                        <Typography variant="h6">Receiver</Typography>

                                        <TextField
                                            select
                                            fullWidth
                                            label="Select Location"
                                            value={newBol.toLocation || ''}
                                            onChange={(e) => {
                                                setNewBol((prev: any) => ({
                                                    ...prev,
                                                    toLocation: e.target.value,
                                                }));
                                                handleReceiverLocationChange(e.target.value);
                                            }}
                                        >
                                            {storeOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        <TextField
                                            fullWidth
                                            label="Name"
                                            placeholder="Enter receiver"
                                            value={newBol.receiver?.name || ''}
                                            onChange={(e) =>
                                                setNewBol((prev: any) => ({
                                                    ...prev,
                                                    receiver: { ...prev.receiver, name: e.target.value },
                                                }))
                                            }
                                        />

                                        <TextField
                                            fullWidth
                                            label="Address"
                                            placeholder="Enter receiver address"
                                            value={newBol.receiver?.address || ''}
                                            onChange={(e) =>
                                                setNewBol((prev: any) => ({
                                                    ...prev,
                                                    receiver: { ...prev.receiver, address: e.target.value },
                                                }))
                                            }
                                        />

                                        <TextField
                                            fullWidth
                                            label="City, Postal Code"
                                            placeholder="Enter receiver postal code"
                                            value={newBol.receiver?.postal_code || ''}
                                            onChange={(e) =>
                                                setNewBol((prev: any) => ({
                                                    ...prev,
                                                    receiver: { ...prev.receiver, postal_code: e.target.value },
                                                }))
                                            }
                                        />
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Details */}
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Stack spacing={2}>
                                    <TextField
                                        fullWidth
                                        label="Amount"
                                        placeholder="Enter amount"
                                        value={newBol.amount || ''}
                                        onChange={(e) =>
                                            setNewBol((prev: any) => ({
                                                ...prev,
                                                amount: e.target.value,
                                            }))
                                        }
                                    />

                                    <TextField
                                        fullWidth
                                        label="Seal No"
                                        placeholder="Enter seal no"
                                        value={newBol?.info?.seal_no || ''}
                                        onChange={(e) =>
                                            setNewBol((prev: any) => ({
                                                ...prev,
                                                info: { ...prev.info, seal_no: e.target.value },
                                            }))
                                        }
                                    />

                                    <TextField
                                        fullWidth
                                        label="Trailer No"
                                        placeholder="Enter trailer no"
                                        value={newBol?.info?.trailer_no || ''}
                                        onChange={(e) =>
                                            setNewBol((prev: any) => ({
                                                ...prev,
                                                info: { ...prev.info, trailer_no: e.target.value },
                                            }))
                                        }
                                    />

                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="BOL Date"
                                        InputLabelProps={{ shrink: true }}
                                        value={newBol.bol_date || ''}
                                        onChange={(e) =>
                                            setNewBol((prev: any) => ({
                                                ...prev,
                                                bol_date: e.target.value,
                                            }))
                                        }
                                    />
                                </Stack>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <Stack spacing={2}>
                                    <TextField
                                        fullWidth
                                        label="Carrier"
                                        placeholder="Enter carrier"
                                        value={newBol.trailer || ''}
                                        onChange={(e) =>
                                            setNewBol((prev: any) => ({
                                                ...prev,
                                                trailer: e.target.value,
                                            }))
                                        }
                                    />

                                    <TextField
                                        select
                                        fullWidth
                                        label="Load/Unload Type"
                                        value={newBol.type || ''}
                                        onChange={(e) =>
                                            setNewBol((prev: any) => ({
                                                ...prev,
                                                type: e.target.value,
                                            }))
                                        }
                                    >
                                        <MenuItem value="LOAD">LOAD</MenuItem>
                                        <MenuItem value="UNLOAD">UNLOAD</MenuItem>
                                        <MenuItem value="DROP OFF">DROP OFF</MenuItem>
                                        <MenuItem value="RELOAD">RELOAD</MenuItem>
                                        <MenuItem value="OTHER">OTHER</MenuItem>
                                    </TextField>

                                    <TextField
                                        fullWidth
                                        label="Description"
                                        placeholder="Enter description"
                                        value={newBol.description || ''}
                                        onChange={(e) =>
                                            setNewBol((prev: any) => ({
                                                ...prev,
                                                description: e.target.value,
                                            }))
                                        }
                                    />

                                    <TextField
                                        fullWidth
                                        label="Special Instruction"
                                        placeholder="Enter note"
                                        value={newBol.note || ''}
                                        onChange={(e) =>
                                            setNewBol((prev: any) => ({
                                                ...prev,
                                                note: e.target.value,
                                            }))
                                        }
                                    />
                                </Stack>
                            </Grid>
                        </Grid>

                        {/* PO Section */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Stack spacing={2}>
                                {/* Header */}
                                <Grid container spacing={2} alignItems="center">
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="h6">PO Details</Typography>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="No of POs"
                                            // inputProps={{ min: newBol?.po?.length }}
                                            value={newBol?.po?.length || 0}
                                            onChange={(e) => {
                                                const poQty = Math.max(1, parseInt(e.target.value || '1', 10));
                                                setNewBol((prev: any) => ({
                                                    ...prev,
                                                    po: Array.from(
                                                        { length: poQty },
                                                        (_, index) => prev.po?.[index] || { po_no: '', skid: '' }
                                                    ),
                                                }));
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                {/* PO Blocks */}
                                <Grid container spacing={2}>
                                    {(newBol.po || []).map((po: any, index: number) => (
                                        <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    position: 'relative',
                                                }}
                                            >
                                                <Stack spacing={1.5}>
                                                    {/* Index Badge */}
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 10,
                                                            fontWeight: 600,
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        #{index + 1}
                                                    </Typography>

                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        PO #{index + 1}
                                                    </Typography>

                                                    <TextField
                                                        size="small"
                                                        label="PO Number"
                                                        value={po?.po_no || ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                            handlePoUpdate('po_no', index, e)
                                                        }
                                                    />
                                                    <TextField
                                                        size="small"
                                                        label="No of Skids"
                                                        type="number"
                                                        inputProps={{ min: 0 }}
                                                        value={po?.skid || ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                            handlePoUpdate('skid', index, e)
                                                        }
                                                    />
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Stack>
                        </Paper>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCloseCreateModal} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={createBolHandler} variant="contained">
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Modal */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>Edit BOL</DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {/* Locked BOL No */}
                        <TextField
                            fullWidth
                            label="BOL No"
                            value={bol.bol_no || ''}
                            disabled
                        />

                        {/* BOL Type */}
                        <ToggleButtonGroup
                            value={bol.bol_type || 'INTERNAL'}
                            exclusive
                            fullWidth
                            onChange={(_, value) => {
                                if (!value) return;
                                setBol((prev: any) => ({
                                    ...prev,
                                    bol_type: value,
                                    supplier: value === 'EXTERNAL' ? prev.supplier || '' : '',
                                }));
                            }}
                            sx={{
                                backgroundColor: 'grey.100',
                                borderRadius: 2,
                                p: 0.5,
                            }}
                        >
                            <ToggleButton
                                value="INTERNAL"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 1.5,
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main',
                                        color: '#fff',
                                    },
                                }}
                            >
                                Internal
                            </ToggleButton>

                            <ToggleButton
                                value="EXTERNAL"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 1.5,
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main',
                                        color: '#fff',
                                    },
                                }}
                            >
                                External
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {/* Supplier */}
                        {bol.bol_type === 'EXTERNAL' && (
                            <TextField
                                fullWidth
                                label="Supplier"
                                placeholder="Enter supplier"
                                value={bol.supplier || ''}
                                onChange={(e) =>
                                    setBol((prev: any) => ({
                                        ...prev,
                                        supplier: e.target.value,
                                    }))
                                }
                            />
                        )}

                        {/* Shipper / Receiver */}
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Stack spacing={2}>
                                        <Typography variant="h6">Shipper</Typography>

                                        <TextField
                                            fullWidth
                                            label="Name"
                                            placeholder="Enter shipper"
                                            value={bol.shipper?.name || ''}
                                            onChange={(e) =>
                                                setBol((prev: any) => ({
                                                    ...prev,
                                                    shipper: { ...prev.shipper, name: e.target.value },
                                                }))
                                            }
                                        />

                                        <TextField
                                            fullWidth
                                            label="Address"
                                            placeholder="Enter shipper address"
                                            value={bol.shipper?.address || ''}
                                            onChange={(e) =>
                                                setBol((prev: any) => ({
                                                    ...prev,
                                                    shipper: { ...prev.shipper, address: e.target.value },
                                                }))
                                            }
                                        />

                                        <TextField
                                            fullWidth
                                            label="City, Postal Code"
                                            placeholder="Enter shipper postal code"
                                            value={bol.shipper?.postal_code || ''}
                                            onChange={(e) =>
                                                setBol((prev: any) => ({
                                                    ...prev,
                                                    shipper: { ...prev.shipper, postal_code: e.target.value },
                                                }))
                                            }
                                        />
                                    </Stack>
                                </Paper>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Stack spacing={2}>
                                        <Typography variant="h6">Receiver</Typography>

                                        <TextField
                                            fullWidth
                                            label="Name"
                                            placeholder="Enter receiver"
                                            value={bol.receiver?.name || ''}
                                            onChange={(e) =>
                                                setBol((prev: any) => ({
                                                    ...prev,
                                                    receiver: { ...prev.receiver, name: e.target.value },
                                                }))
                                            }
                                        />

                                        <TextField
                                            fullWidth
                                            label="Address"
                                            placeholder="Enter receiver address"
                                            value={bol.receiver?.address || ''}
                                            onChange={(e) =>
                                                setBol((prev: any) => ({
                                                    ...prev,
                                                    receiver: { ...prev.receiver, address: e.target.value },
                                                }))
                                            }
                                        />

                                        <TextField
                                            fullWidth
                                            label="City, Postal Code"
                                            placeholder="Enter receiver postal code"
                                            value={bol.receiver?.postal_code || ''}
                                            onChange={(e) =>
                                                setBol((prev: any) => ({
                                                    ...prev,
                                                    receiver: { ...prev.receiver, postal_code: e.target.value },
                                                }))
                                            }
                                        />
                                        <TextField
                                            fullWidth
                                            label="Corporation No."
                                            placeholder="Enter corporation no"
                                            value={bol.corporation_no || ''}
                                            onChange={(e) =>
                                                setBol((prev: any) => ({
                                                    ...prev,
                                                    corporation_no: e.target.value
                                                }))
                                            }
                                        />
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Main Details */}
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Stack spacing={2}>
                                    <TextField
                                        fullWidth
                                        label="Amount"
                                        placeholder="Enter amount"
                                        value={bol.amount || ''}
                                        onChange={(e) =>
                                            setBol((prev: any) => ({
                                                ...prev,
                                                amount: e.target.value,
                                            }))
                                        }
                                    />

                                    <TextField
                                        fullWidth
                                        label="Seal No"
                                        placeholder="Enter seal no"
                                        value={bol?.info?.seal_no || ''}
                                        onChange={(e) =>
                                            setBol((prev: any) => ({
                                                ...prev,
                                                info: { ...prev.info, seal_no: e.target.value },
                                            }))
                                        }
                                    />

                                    <TextField
                                        fullWidth
                                        label="Trailer No"
                                        placeholder="Enter trailer no"
                                        value={bol?.info?.trailer_no || ''}
                                        onChange={(e) =>
                                            setBol((prev: any) => ({
                                                ...prev,
                                                info: { ...prev.info, trailer_no: e.target.value },
                                            }))
                                        }
                                    />

                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="BOL Date"
                                        InputLabelProps={{ shrink: true }}
                                        value={bol.bol_date?.split('T')[0] || ''}
                                        onChange={(e) =>
                                            setBol((prev: any) => ({
                                                ...prev,
                                                bol_date: e.target.value,
                                            }))
                                        }
                                    />
                                </Stack>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <Stack spacing={2}>
                                    <TextField
                                        fullWidth
                                        label="Carrier"
                                        placeholder="Enter carrier"
                                        value={bol.trailer || ''}
                                        onChange={(e) =>
                                            setBol((prev: any) => ({
                                                ...prev,
                                                trailer: e.target.value,
                                            }))
                                        }
                                    />

                                    <TextField
                                        select
                                        fullWidth
                                        label="Load/Unload Type"
                                        value={bol.type || ''}
                                        onChange={(e) =>
                                            setBol((prev: any) => ({
                                                ...prev,
                                                type: e.target.value,
                                            }))
                                        }
                                    >
                                        <MenuItem value="LOAD">LOAD</MenuItem>
                                        <MenuItem value="UNLOAD">UNLOAD</MenuItem>
                                        <MenuItem value="DROP OFF">DROP OFF</MenuItem>
                                        <MenuItem value="RELOAD">RELOAD</MenuItem>
                                        <MenuItem value="OTHER">OTHER</MenuItem>
                                    </TextField>

                                    <TextField
                                        fullWidth
                                        label="Description"
                                        placeholder="Enter description"
                                        value={bol.description || ''}
                                        onChange={(e) =>
                                            setBol((prev: any) => ({
                                                ...prev,
                                                description: e.target.value,
                                            }))
                                        }
                                    />

                                    <TextField
                                        fullWidth
                                        label="Special Instruction"
                                        placeholder="Enter note"
                                        value={bol.note || ''}
                                        onChange={(e) =>
                                            setBol((prev: any) => ({
                                                ...prev,
                                                note: e.target.value,
                                            }))
                                        }
                                    />
                                </Stack>
                            </Grid>
                        </Grid>

                        {/* Cancel Status */}
                        <TextField
                            select
                            fullWidth
                            label="Delivery Status"
                            value={String(!!bol.cancel)}
                            onChange={(e) =>
                                setBol((prev: any) => ({
                                    ...prev,
                                    cancel: e.target.value === 'true',
                                }))
                            }
                        >
                            <MenuItem value="false">Active</MenuItem>
                            <MenuItem value="true">Cancelled</MenuItem>
                        </TextField>

                        {/* PO Section */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Stack spacing={2}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="h6">PO Details</Typography>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="No of POs"
                                            inputProps={{ min: 1 }}
                                            value={bol.po?.length || 1}
                                            onChange={(e) => {
                                                const poQty = Math.max(1, parseInt(e.target.value || '1', 10));
                                                setBol((prev: any) => ({
                                                    ...prev,
                                                    po: Array.from(
                                                        { length: poQty },
                                                        (_, index) => prev.po?.[index] || { po_no: '', skid: '' }
                                                    ),
                                                }));
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2}>
                                    {(bol.po || []).map((item: any, index: number) => (
                                        <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    position: 'relative',
                                                }}
                                            >
                                                <Stack spacing={1.5}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 10,
                                                            fontWeight: 600,
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        #{index + 1}
                                                    </Typography>

                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        PO #{index + 1}
                                                    </Typography>

                                                    <TextField
                                                        size="small"
                                                        label="PO Number"
                                                        value={item.po_no || ''}
                                                        onChange={(e) =>
                                                            handleExistingPoUpdate('po_no', index, e.target.value)
                                                        }
                                                    />

                                                    <TextField
                                                        size="small"
                                                        label="No of Skids"
                                                        type="number"
                                                        inputProps={{ min: 0 }}
                                                        value={item.skid || ''}
                                                        onChange={(e) =>
                                                            handleExistingPoUpdate('skid', index, e.target.value)
                                                        }
                                                    />

                                                    <Button
                                                        color="error"
                                                        variant="outlined"
                                                        onClick={() => handleRemovePo(index)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>

                                <Stack direction="row" justifyContent="flex-end">
                                    <Button variant="outlined" onClick={handleAddPo}>
                                        Add PO
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <BolPrint bol={bol} />
                    <Button variant="outlined" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleUpdateBol}>
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Temporary location/export controls until moved into BolTopBar */}
            <Box sx={{ display: 'none' }}>
                {DC_LOCATIONS}
            </Box>
            <AppDialogMessage
                open={dialog.open}
                title={dialog.title}
                message={dialog.message}
                variant={dialog.variant}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                hideCancel={dialog.hideCancel}
                onConfirm={dialog.onConfirm}
                onClose={closeDialog}
            />
        </>
    );
}